import React, { useEffect, useRef, useState } from 'react';
import Slider from 'rc-slider';

const pad2 = (n) => String(n).padStart(2, '0');

// 83.4 s -> '01:24' (same rounding the player bar has always used)
const fmtElapsed = (secs) => {
  const t = Number.isFinite(secs) && secs > 0 ? secs : 0;
  let minutes = Math.floor(t / 60);
  let seconds = Math.ceil(t - minutes * 60);
  if (seconds === 60) {
    minutes += 1;
    seconds = 0;
  }
  return pad2(minutes) + ':' + pad2(seconds);
};

// Seek bar and elapsed time for the active <audio> element. It owns the
// 100 ms ticker, so a playing track re-renders only this small component,
// never the whole App (which can hold hundreds of result rows).
//   getEl()     returns the active audio element (stable function)
//   active      something is loaded in the player
//   length      the current item's length label
//   resetKey    changes on every track change (qid + playNonce): back to 0
//   onTick(el)  called on every tick while the element plays; App uses it for
//               the 15 s standby preload rule and to clear the spinner
function PlayerProgress({ getEl, active, length, resetKey, onTick }) {
  const [progress, setProgress] = useState(0);
  const [playtime, setPlaytime] = useState('00:00');
  const seekingRef = useRef(false);
  const getElRef = useRef(getEl);
  getElRef.current = getEl;
  const onTickRef = useRef(onTick);
  onTickRef.current = onTick;
  const activeRef = useRef(active);
  activeRef.current = active;

  const update = (el) => {
    if (seekingRef.current || !el) return;
    const played = el.duration ? el.currentTime / el.duration : 0;
    setPlaytime(fmtElapsed(el.currentTime));
    setProgress(played * 100000);
  };
  const updateRef = useRef(update);
  updateRef.current = update;

  useEffect(() => {
    setProgress(0);
    setPlaytime('00:00');
  }, [resetKey]);

  useEffect(() => {
    const ticker = setInterval(() => {
      const el = getElRef.current ? getElRef.current() : null;
      if (!el || !activeRef.current) return;
      // Unchanged values bail out without a render, so this is free while
      // paused; it also shows a skip made while paused.
      updateRef.current(el);
      if (!el.paused && onTickRef.current) onTickRef.current(el);
    }, 100);
    return () => clearInterval(ticker);
  }, []);

  const onSeekDrag = (value) => {
    seekingRef.current = true;
    setProgress(value);
  };

  const onSeekDone = (value) => {
    seekingRef.current = false;
    const el = getElRef.current ? getElRef.current() : null;
    if (el && activeRef.current && el.duration) {
      el.currentTime = (value / 100000) * el.duration;
      update(el);
    }
  };

  return (
    <div className="scrub">
      <span className="time mono">{playtime}</span>
      <div className="scrub-slider">
        <Slider min={0} max={100000} value={progress} onChange={onSeekDrag} onAfterChange={onSeekDone} disabled={!active} />
      </div>
      <span className="time mono">{active ? length : '00:00'}</span>
    </div>
  );
}

export default React.memo(PlayerProgress);
